<template>
  <div class="post-container">
    <el-card class="post-card">
      <div class="post-wrapper" :style="{ position: 'relative', cursor: disableNavigation ? 'default' : 'pointer' }" @click="viewDetail">
        <div class="post-title">
          <p v-if="post.answers?.[0]?.has_title" class="truncated-title" style="margin: 0;">{{ post.title }}</p>
          <p v-else class="truncated-title" style="margin: 0;">{{ post.title }}</p>
        </div>

        <div class="post-header" style="margin-top: 12px;">
          <span style="font-size: 12px; color: #8c8c8c; font-weight: normal; line-height: 1.8;">
            <span>By</span>
            <el-avatar :size="16" :src="post.authorAvatar" style="margin: 0 6px; vertical-align: middle; position: relative; top: -2px;">
              {{ post.user_name?.charAt(0) }}
            </el-avatar>
            <router-link
              :to="{ path: '/profile', query: { id: post.user_id }}"
              class="user-name-link"
            >
              {{ post.user_name }}{{ post.user_id === $store.state.user_id ? ' (You)' : '' }}
            </router-link><template v-if="post.user_id === $store.state.user_id && post.status === 'PACKAGED'"> • From <i class="el-icon-collection"></i> {{ post.collection_title || post.collection_name || 'Unknown Course' }}</template>
          </span>
        </div>
        
        <div class="post-answer">
          <div class="answer-locked" v-if="post.view_status == 'NO' && post.user_id !== $store.state.user_id">
            <i class="el-icon-lock"></i>
            <span> Cryptic</span>
          </div>
        </div>
        <div class="post-actions">
          <div style="display: flex; gap: 6px; align-items: center; margin-left: 8px;">
            <el-tag
              v-if="post.user_id === $store.state.user_id && post.status === 'PRIVATE'"
              type="info"
              size="small"
              effect="dark"
            >
              PRIVATE
            </el-tag>
            <el-tag type="primary" size="small">
              <i class="el-icon-document"></i>
              {{ post.answers?.length || 0 }}
            </el-tag>
          </div>
          <div style="margin-left: auto;">
            <div class="stats-group">
              <el-button type="text" @click.stop="handleThumbUp" :loading="thumbUpLoading" :class="{ 'is-thumbed-up': isThumbedUp }">
                <i class="el-icon-thumb"></i>
                {{ post.thumb_up_count || 0 }}
              </el-button>
              <el-button type="text" @click.stop="handleMark" :loading="markLoading" :class="{ 'is-marked': isMarked }">
                <i :class="isMarked ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                {{ post.mark_count || 0 }}
              </el-button>
              <el-button type="text">
                <i class="el-icon-share"></i>
                {{ post.share_count || 0 }}
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import { renderFormattedContent } from '@/utils/contentFormatter'

export default {
  name: 'Post',
  props: {
    post: {
      type: Object,
      required: true
    },
    groupOptions: {
      type: Array,
      default: () => []
    },
    scene: {
      type: String,
      required: false,
      default: 'post'
    },
    allowPageTurning: {
      type: Boolean,
      default: false
    },
    disableNavigation: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      markLoading: false,
      thumbUpLoading: false,
      thumbDownLoading: false,
      currentAnswerIndex: 0,
    }
  },
  computed: {
    isMarked() {
      // Check if the current user has marked this post
      if(this.post.mark_status && this.post.mark_status == 'YES') {
        return true
      } else {
        return false
      }
    },
    isThumbedUp() {
      // Check if the current user has thumbed up this post
      if(this.post.thumb_direction && this.post.thumb_direction == 'UP') {
        return true
      } else {
        return false
      }
    },
    isThumbedDown() {
      // Check if the current user has thumbed down this post
      if(this.post.thumb_direction && this.post.thumb_direction == 'DOWN') {
        return true
      } else {
        return false
      }
    }
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString()
    },
    handleLike() {
      this.$emit('like', (post._id.$oid || post._id.$id))
    },
    handleComment() {
      this.$emit('comment', (post._id.$oid || post._id.$id))
    },
    viewDetail() {
      // If navigation is disabled, do nothing
      if (this.disableNavigation) {
        return;
      }
      
       if(this.post.collection_id && this.post.status === 'PACKAGED' && this.scene === 'collection'){
        this.$store.dispatch('setTempCollectionId', this.post.collection_id)
        this.$router.push('/read-collection')
       } else {
        this.$router.push({
          path: '/stack',
          query: { id: (this.post._id.$oid || this.post._id.$id) }
        })
       }
    },
    async exchangeToView() {
      try {
        const params = {
          post_id: (post._id.$oid || post._id.$id),
          viewer_id: this.$store.state.user_id,
          viewee_id: this.post.user_id
        }

        const response = await postRequest('viewPost', params)
        
        if (response.data.code === 0) {
          this.$router.push({
            path: '/post',
            query: { id: (post._id.$oid || post._id.$id), user_id: this.post.user_id }
          })
          this.$message.success('Exchange successful')
          this.$emit('exchange-success')
        } else {
          this.$message.error(response.data.msg || 'Exchange failed')
        }
      } catch (error) {
        this.$message.error('Failed to exchange')
        console.error('Error exchanging:', error)
      }
    },
    getFormattedContent(content) {
      if (!content) return ''
      const result = renderFormattedContent(content)
      return result.html
    },
    getBorderStyleByUserType(userType) {
      // Default to Individual if not set
      const type = userType || 'individual';
      
      switch(type) {
        case 'individual':
          return '2px solid #1890ff'; // Theme blue border
        case 'institution':
          return '2px solid #67C23A'; // Green border for institutions
        case 'agent':
          return '2px solid #E6A23C'; // Orange border for agents
        default:
          return '1px solid #e0e0e0'; // Default gray border
      }
    },
    async handleMark() {
      if (!this.$store.state.user_id) {
        this.$emit('login-required')
        return
      }
      
      this.markLoading = true;
      try {
        const params = {
          user_id: this.$store.state.user_id,
          post_id: (post._id.$oid || post._id.$id)
        }

        let response;
        if (this.isMarked) {
          // Unmark the post
          response = await postRequest('removeMarkPost', params)
        } else {
          // Mark the post
          response = await postRequest('markPost', params)
        }
        
        if (response.data.code === 0) {
          // Update the local state
          if(!this.isMarked) {
            this.$set(this.post, 'mark_status', 'YES')
          } else {
            this.$set(this.post, 'mark_status', 'NO')
          }
          
          // Update the mark count
          if (!this.isMarked) {
            this.$set(this.post, 'mark_count', Math.max(0, (this.post.mark_count || 0) - 1))
          } else {
            this.$set(this.post, 'mark_count', (this.post.mark_count || 0) + 1)
          }
          
          // this.$message.success(this.isMarked ? 'Post marked' : 'Post unmarked')
          // this.$emit('mark-status-update', { post: this.post, isMarked: !this.isMarked })
        } else {
          this.$message.error(response.data.msg || 'Failed to update mark status')
        }
      } catch (error) {
        this.$message.error('Failed to update mark status')
        console.error('Error updating mark status:', error)
      } finally {
        this.markLoading = false;
      }
    },
    async handleThumbUp() {
      if (!this.$store.state.user_id) {
        this.$emit('login-required')
        return
      }
      
      this.thumbUpLoading = true;
      try {
        const params = {
          user_id: this.$store.state.user_id,
          post_id: (post._id.$oid || post._id.$id),
          direction: this.isThumbedUp ? 'NO' : 'UP'
        }

        const response = await postRequest('thumbPost', params)
        
        if (response.data.code === 0) {
          // Update the local state
          if(this.isThumbedUp) {
            // Was thumbed up, now remove it
            this.$delete(this.post, 'thumb_direction')
            this.$set(this.post, 'thumb_up_count', Math.max(0, (this.post.thumb_up_count || 0) - 1))
          } else {
            // Was not thumbed up, now add it
            if(this.post.thumb_direction && this.post.thumb_direction == 'DOWN') {
              this.$set(this.post, 'thumb_down_count', Math.max(0, (this.post.thumb_down_count || 0) - 1))
            }
            this.$set(this.post, 'thumb_direction', 'UP')
            this.$set(this.post, 'thumb_up_count', (this.post.thumb_up_count || 0) + 1)
            // If was thumbed down, remove that count
            if(this.isThumbedDown) {
              this.$set(this.post, 'thumb_down_count', Math.max(0, (this.post.thumb_down_count || 0) - 1))
            }
          }
        } else {
          this.$message.error(response.data.msg || 'Failed to update thumb up status')
        }
      } catch (error) {
        this.$message.error('Failed to update thumb up status')
        console.error('Error updating thumb up status:', error)
      } finally {
        this.thumbUpLoading = false;
      }
    },
    async handleThumbDown() {
      if (!this.$store.state.user_id) {
        this.$emit('login-required')
        return
      }
      
      this.thumbDownLoading = true;
      try {
        const params = {
          user_id: this.$store.state.user_id,
          post_id: (post._id.$oid || post._id.$id),
          direction: this.isThumbedDown ? 'NO' : 'DOWN'
        }

        const response = await postRequest('thumbPost', params)
        
        if (response.data.code === 0) {
          // Update the local state
          if(this.isThumbedDown) {
            // Was thumbed down, now remove it
            this.$delete(this.post, 'thumb_direction')
            this.$set(this.post, 'thumb_down_count', Math.max(0, (this.post.thumb_down_count || 0) - 1))
          } else {
            // Was not thumbed down, now add it
            if(this.post.thumb_direction && this.post.thumb_direction == 'UP') {
              this.$set(this.post, 'thumb_up_count', Math.max(0, (this.post.thumb_up_count || 0) - 1))
            }
            this.$set(this.post, 'thumb_direction', 'DOWN')
            this.$set(this.post, 'thumb_down_count', (this.post.thumb_down_count || 0) + 1)
            // If was thumbed up, remove that count
            if(this.isThumbedUp) {
              this.$set(this.post, 'thumb_up_count', Math.max(0, (this.post.thumb_up_count || 0) - 1))
            }
          }
        } else {
          this.$message.error(response.data.msg || 'Failed to update thumb down status')
        }
      } catch (error) {
        this.$message.error('Failed to update thumb down status')
        console.error('Error updating thumb down status:', error)
      } finally {
        this.thumbDownLoading = false;
      }
    },
    nextAnswer() {
      if (this.currentAnswerIndex < this.post.answers.length - 1) {
        this.currentAnswerIndex++;
      }
    },
    previousAnswer() {
      if (this.currentAnswerIndex > 0) {
        this.currentAnswerIndex--;
      }
    }
  },
  created() {
    // Initialize mark status if not provided
    if (this.post.mark_status === undefined) {
      this.$set(this.post, 'mark_status', 'NO')
    }
    // Initialize thumb status if not provided
    if (this.post.thumb_status === undefined) {
      this.$set(this.post, 'thumb_status', 'NO')
    }
  }
}
</script>

<style scoped>
.post-card {
  width: 400px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.post-card :deep(.el-card__body) {
  padding: 20px 20px 0 20px;
}

.post-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);
}

.post-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  position: relative;
}

.post-info {
  margin-left: 8px;
}

.post-info h3 {
  margin: 0;
  font-size: 14px;
}

.post-meta {
  font-size: 14px;
  color: #8c8c8c;
}

.post-meta .dot {
  margin: 0 8px;
}

.post-content {
  margin-bottom: 15px;
}

.post-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #f0f0f0;
  padding: 12px;
  margin: 0 -20px;
  background-color: white;
}

.action-buttons {
  margin-left: auto;
  display: flex;
  gap: 16px;
}

.action-buttons.align-left {
  margin-left: 0;
  margin-right: auto;
}

.stats-group {
  display: flex;
  gap: 0;
}

.stats-group .el-button {
  padding: 4px 6px;
  font-size: 12px;
  color: #606266;
  min-width: auto;
}

.stats-group .el-button i {
  font-size: 14px;
}

.stats-group .el-button:hover {
  color: #409eff;
}

.stats-group .el-button.is-marked {
  color: #1159AF;
}

.stats-group .el-button.is-marked:hover {
  color: #1159AF;
}

.stats-group .el-button.is-thumbed-up {
  color: #1159AF;
}

.stats-group .el-button.is-thumbed-up:hover {
  color: #1159AF;
}

.stats-group .el-button.is-thumbed-down {
  color: #1159AF;
}

.stats-group .el-button.is-thumbed-down:hover {
  color: #1159AF;
}

.post-wrapper {
  /* cursor handled dynamically via :style */
}

.post-title p {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #303133;
  line-height: 1.4;
}

.post-description p {
  font-size: 14px;
  color: #606266;
  margin: 0 0 15px 0;
  line-height: 1.6;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.post-status {
  position: absolute;
  right: 0;
  top: 0;
}

.post-status .el-tag {
  font-size: 11px;
}

.user-name-link {
  color: inherit;
  text-decoration: none;
  font-weight: normal;
}

.user-name-link:hover {
  color: inherit;
  text-decoration: none;
}

.note-text {
  font-size: 12px;
  color: #8c8c8c;
} 

.answer-text {
  color: #666666;
}

.answer-preview {
  padding: 12px;
  font-size: 13px;
  color: #666666;
  line-height: 1.6;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

.answer-text :deep(h1),
.answer-text :deep(h2),
.answer-text :deep(h3),
.answer-text :deep(h4),
.answer-text :deep(h5),
.answer-text :deep(h6) {
  margin-top: 0.8em;
  margin-bottom: 0.5em;
  color: #303133;
  font-weight: 600;
}

.answer-text :deep(h1) { font-size: 1.8em; }
.answer-text :deep(h2) { font-size: 1.5em; }
.answer-text :deep(h3) { font-size: 1.3em; }
.answer-text :deep(h4) { font-size: 1.1em; }
.answer-text :deep(h5) { font-size: 1em; }
.answer-text :deep(h6) { font-size: 0.9em; }

.answer-text :deep(p) {
  margin: 0.5em 0;
  line-height: 1.6;
}

.answer-text :deep(code) {
  background-color: #f5f5f5;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 0.9em;
  color: #e83e8c;
}

.answer-text :deep(pre) {
  background-color: #f8f9fa;
  padding: 1em;
  border-radius: 4px;
  overflow-x: auto;
  margin: 1em 0;
  border: 1px solid #e9ecef;
}

.answer-text :deep(pre code) {
  background-color: transparent;
  padding: 0;
  color: inherit;
}

.answer-text :deep(blockquote) {
  border-left: 4px solid #409eff;
  padding-left: 1em;
  margin: 1em 0;
  color: #606266;
  background-color: #f5f7fa;
  padding: 0.5em 1em;
  border-radius: 0 4px 4px 0;
}

.answer-text :deep(ul),
.answer-text :deep(ol) {
  padding-left: 2em;
  margin: 0.5em 0;
}

.answer-text :deep(li) {
  margin: 0.3em 0;
  line-height: 1.6;
}

.answer-text :deep(a) {
  color: #409eff;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.3s;
}

.answer-text :deep(a:hover) {
  border-bottom-color: #409eff;
}

.answer-text :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1em 0;
}

.answer-text :deep(th),
.answer-text :deep(td) {
  border: 1px solid #dcdfe6;
  padding: 8px 12px;
  text-align: left;
}

.answer-text :deep(th) {
  background-color: #f5f7fa;
  font-weight: 600;
  color: #303133;
}

.answer-text :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 0.5em 0;
}

.answer-text :deep(hr) {
  border: none;
  border-top: 1px solid #e4e7ed;
  margin: 1.5em 0;
}

.answer-text :deep(strong) {
  font-weight: 600;
  color: #303133;
}

.answer-text :deep(em) {
  font-style: italic;
}

.answer-text :deep(del) {
  text-decoration: line-through;
  color: #909399;
}

.cryptic-placeholder {
  width: 100%;
  aspect-ratio: 0.707;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 16px;
  gap: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.cryptic-placeholder:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.15);
}

.cryptic-placeholder i {
  font-size: 32px;
  margin-bottom: 8px;
}

.truncated-title {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.answer-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

.answer-counter {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}
</style> 